import { useCallback, useEffect, useRef, useState } from 'react';
import { adminMockCreditsAPI, MockCreditTicketStats } from '../services/api';
import { MOCK_CREDIT_TICKETS_UPDATED_EVENT } from '../utils/adminMockCreditEvents';

const POLL_MS = 45_000;

const EMPTY_STATS: MockCreditTicketStats = {
  open_total: 0,
  awaiting_admin: 0,
  awaiting_user: 0,
};

export function useAdminMockCreditTicketCounts(enabled = true) {
  const [stats, setStats] = useState<MockCreditTicketStats>(EMPTY_STATS);
  const [loading, setLoading] = useState(true);
  const mountedRef = useRef(true);

  const refresh = useCallback(async () => {
    if (!enabled) return;
    try {
      const data = await adminMockCreditsAPI.getStats();
      if (mountedRef.current) {
        setStats(data);
      }
    } catch {
      /* silent — badge is non-critical */
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [enabled]);

  useEffect(() => {
    mountedRef.current = true;
    if (!enabled) {
      setLoading(false);
      return () => {
        mountedRef.current = false;
      };
    }

    refresh();
    const onUpdate = () => {
      refresh();
    };
    const onFocus = () => {
      refresh();
    };
    window.addEventListener(MOCK_CREDIT_TICKETS_UPDATED_EVENT, onUpdate);
    window.addEventListener('focus', onFocus);
    const interval = window.setInterval(refresh, POLL_MS);

    return () => {
      mountedRef.current = false;
      window.removeEventListener(MOCK_CREDIT_TICKETS_UPDATED_EVENT, onUpdate);
      window.removeEventListener('focus', onFocus);
      window.clearInterval(interval);
    };
  }, [enabled, refresh]);

  return { stats, loading, refresh };
}
